import { groupBy } from 'lodash'
import { SingleItem } from 'redux/slices/single-items-slice'

export const MOUNJARO_STARTER_PROMOTION_ID = 'mounjaro-starter-ecs709'

const PROMOTION_PRODUCT_ID = -70901
const EXPECTED_PROMOTION_SKUS = ['mounjaro-promo-inicio-2-5mg', 'mounjaro-promo-inicio-5mg']

export const bundleMounjaroStarterPromotion = (items: SingleItem[]): SingleItem[] => {
  const promotionItems = items.filter((item) => item.promotion?.id === MOUNJARO_STARTER_PROMOTION_ID)
  const ordinaryItems = items.filter((item) => item.promotion?.id !== MOUNJARO_STARTER_PROMOTION_ID)
  const orderedPromotionItems = EXPECTED_PROMOTION_SKUS.map((sku) =>
    promotionItems.find((item) => String(item.id) === sku)
  ).filter(Boolean) as SingleItem[]

  if (orderedPromotionItems.length !== EXPECTED_PROMOTION_SKUS.length) return ordinaryItems

  const baseItem = orderedPromotionItems[0]
  const promotion = baseItem.promotion
  if (!promotion) return ordinaryItems

  return [
    ...ordinaryItems,
    {
      ...baseItem,
      id: MOUNJARO_STARTER_PROMOTION_ID,
      name: 'Promoção Mounjaro',
      commercialName: 'Promoção Mounjaro',
      description: 'Kit com 1 caixa de Mounjaro 2,5 mg e 1 caixa de Mounjaro 5 mg.',
      presentation: '2 caixas: Mounjaro 2,5 mg + Mounjaro 5 mg',
      productId: PROMOTION_PRODUCT_ID,
      specialtyNames: orderedPromotionItems.map((item) => item.commercialName || item.name),
      variantId: orderedPromotionItems[0].variantId,
      variantIds: orderedPromotionItems.map((item) => item.variantId),
      singleItemPrice: promotion.promotionalTotal,
      price: promotion.promotionalTotal,
      type: 'drugs',
      promotion
    }
  ]
}

// ECS-879 — promos 2 (7,5/10 mg) and 3 (12,5/15 mg). Nest lists one variant per
// prescribed dose with the promo and regular prices and `maxBoxes` (boxes of that
// dose the patient's prescriptions cover). Each promotion is sold as 2 boxes:
// 2x one dose, 2x the other, or one of each.
export const MOUNJARO_COMBO_BOXES = 2

const COMBO_PRODUCT_IDS: Record<string, number> = {
  'mounjaro-promo-2-individualizacao': -70902,
  'mounjaro-promo-3-individualizacao': -70903
}

export const isMounjaroComboOffer = (item: Pick<SingleItem, 'promotion'>) => item.promotion?.kind === 'comboOffer'

// The cart persists across sessions: a saved combo the refreshed catalog no
// longer offers (receita used or expired, promotion switched off) is dropped.
export const dropStaleMounjaroCombos = (cart: SingleItem[], singles: SingleItem[]) => {
  const offered = new Set(singles.filter(isMounjaroComboOffer).map((item) => item.id))
  return cart.filter(
    (line) =>
      !(isMounjaroComboOffer(line) || Object.values(COMBO_PRODUCT_IDS).includes(Number(line.productId))) ||
      offered.has(line.id)
  )
}

export const getMounjaroDose = (item: SingleItem) => {
  const text = [item.presentation, item.commercialName, item.name].filter(Boolean).join(' ')
  const raw = text.match(/(\d+(?:[.,]\d+)?)\s*mg/i)?.[1]
  return raw ? String(Number(raw.replace(',', '.'))).replace('.', ',') : ''
}

const doseValue = (item: SingleItem) => Number(getMounjaroDose(item).replace(',', '.'))

export const bundleMounjaroComboPromotions = (items: SingleItem[]): SingleItem[] => {
  const comboItems = items.filter((item) => item.promotion?.kind === 'combo')
  if (!comboItems.length) return items

  const offers = Object.entries(groupBy(comboItems, (item) => item.promotion?.id)).flatMap(([promotionId, doses]) => {
    const sorted = [...doses].sort((left, right) => doseValue(left) - doseValue(right))
    // One box of each dose needs two receitas or one marked "uso contínuo"
    // (Nest's mixedPairAllowed); otherwise each receita covers one box.
    const pairs: SingleItem[][] = [
      ...sorted.filter((item) => (item.promotion?.maxBoxes ?? 1) >= MOUNJARO_COMBO_BOXES).map((item) => [item, item]),
      ...(sorted.length >= 2 && sorted[0].promotion?.mixedPairAllowed !== false ? [[sorted[0], sorted[1]]] : [])
    ]

    return pairs.map((pair) => {
      const [first, second] = pair
      const unit = (item: SingleItem) => Number(item.promotion?.unitPrice ?? item.singleItemPrice ?? item.price)
      const regularUnit = (item: SingleItem) => Number(item.promotion?.regularUnitPrice ?? unit(item))
      const promotionalTotal = unit(first) + unit(second)
      const regularTotal = regularUnit(first) + regularUnit(second)
      const presentation =
        first === second
          ? `2 caixas de Mounjaro ${getMounjaroDose(first)} mg`
          : `1 caixa de Mounjaro ${getMounjaroDose(first)} mg + 1 caixa de ${getMounjaroDose(second)} mg`
      const doses =
        first === second
          ? `2 caixas de ${getMounjaroDose(first)} mg`
          : `${getMounjaroDose(first)} mg + ${getMounjaroDose(second)} mg`
      // Cart and checkout summaries show only the name, so it carries the doses.
      const name = `Promoção Mounjaro · ${doses}`

      return {
        ...first,
        id: `${promotionId}:${first.id}+${second.id}`,
        name,
        commercialName: name,
        description: presentation,
        presentation,
        productId: COMBO_PRODUCT_IDS[promotionId] ?? first.productId,
        // The specialist tabs match prescribed molecules ("Mounjaro 7,5mg").
        specialtyNames: [first.commercialName || first.name, second.commercialName || second.name],
        variantId: first.variantId,
        variantIds: [first.variantId, second.variantId],
        singleItemPrice: promotionalTotal,
        price: promotionalTotal,
        type: 'drugs' as const,
        promotion: {
          ...first.promotion!,
          kind: 'comboOffer' as const,
          regularTotal,
          promotionalTotal,
          savings: Math.max(0, regularTotal - promotionalTotal)
        }
      }
    })
  })

  return [...items.filter((item) => item.promotion?.kind !== 'combo'), ...offers]
}
